import _ from "lodash";
import React, {ReactElement, useCallback, useState} from "react";
import AssetsUtil from "../util/AssetsUtil";
import OpsChapterPath from "../vault/OpsChapterPath";
import OpsPage from "../vault/OpsPage";
import OpsUser from "../vault/OpsUser";
import StudentLink from "./StudentLink";
import {Page, User} from "shared/types/VaultTypes";
import URLMake from "shared/URLMake";
import GradeModal from "./GradeModal";
import Modal from "./Modal";
import SettingsModal from "./SettingsModal";
import {AT} from "../vault/ActionTypeConstants";
const {ICON_MAP} = require("../util/NavIcons");
import Sounds from "../util/Sounds.js";
import {Portal} from "./Portal";
import {PORTAL_CONTAINERS} from "../../../shared/PortalConstants";

import * as styles from "./styles/LeftSideNav.module.css";
import LeaderboardsModal from "./LeaderboardsModal";
import {LeaderboardsLocked} from "../util/components/LeaderboardsLocked";
import {useVaultUpdate} from "../util/useVaultUpdate";
import {atCurrResult} from "../vault/StateCursors";

const dbDemoData = require("../../../shared/data/dbDemoData.json");
const Avatar = require("./Avatar");
const MessageModal = require("./MessageModal");
const NavButton = require("./NavButton").default;
const {FLAGS, FLAG_OPTIONS} = require("../../core/util/GlobalFlags");

const LeftSideNavButton = ({
	iconSrc,
	icon,
	label,
	handleClick,
	linkTo,
	clickSound,
	isSelected = false,
	children,
}: {
	iconSrc?: string;
	icon?: ReactElement;
	label: string;
	handleClick?: () => void;
	linkTo?: string | null;
	clickSound: string;
	isSelected?: boolean;
	children?: ReactElement | null;
}) => {
	const onClick = useCallback(() => {
		handleClick?.();
		Sounds.playSound(clickSound);
	}, [clickSound, handleClick]);
	const content = (
		<div className={styles.navButtonContent} onClick={onClick}>
			{iconSrc && <img className={styles.navButtonIcon} src={iconSrc} />}
			{icon}
			<div className={styles.navButtonLabel}>{label}</div>
		</div>
	);
	return (
		<div
			className={
				styles.navButton + (isSelected ? ` ${styles.navButtonSelected}` : "")
			}
		>
			{linkTo ? (
				<div className={styles.navButtonContentWrapper}>
					<StudentLink
						to={linkTo}
						useWonkyCursor={true}
						className={styles.navButtonContentLink}
					>
						{content}
					</StudentLink>
				</div>
			) : (
				content
			)}
			{children}
		</div>
	);
};

export const LeftSideNav = ({
	user,
	page,
	dispatch,
}: {
	user: User;
	page: Page;
	dispatch: (action: any) => void;
}) => {
	const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
	const [isLeaderboardsModalOpen, setIsLeaderboardsModalOpen] = useState(false);
	const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
	const [isGradeModalOpen, setIsGradeModalOpen] = useState(false);
	const year = new Date().getFullYear();
	const result = useVaultUpdate({
		getValuesFromVault: vault => atCurrResult(vault).get(),
	});
	const classURL = useVaultUpdate({
		getValuesFromVault: vault => OpsChapterPath.getChapterURLForAvatar(vault),
	});
	const libraryURL = page.demo
		? URLMake.forDemoLibraryGrade()
		: URLMake.forDefaultLibraryGrade(user);

	const toggleMoreDropdown = useCallback(() => {
		setIsMoreDropdownOpen(prev => !prev);
	}, []);
	const openSettingsModal = useCallback(() => {
		setIsSettingsModalOpen(true);
		dispatch({type: AT.OPEN_SETTINGS_MODAL});
	}, [dispatch]);

	const closeSettingsModal = useCallback(() => {
		setIsSettingsModalOpen(false);
		dispatch({type: AT.CLOSE_MODAL_IN_THEATER});
	}, [dispatch]);

	const openLeaderboardsModal = () => {
		if (
			OpsPage.isCurrentlyTakingTest(user, page) &&
			!OpsUser.hasKey(user, "trainer allow unfinished test")
		) {
			const messageType =
				result && result.resultType === "bookTest"
					? "finishBookTest"
					: "finishTestOrQuit";
			dispatch({
				type: AT.SHOW_CONFIRM,
				messageType,
				data: {
					onConfirmDispatch: {
						type: AT.SET_PAGE,
						pageState: URLMake.state.forOverview(result),
					},
					styleType: "grade" + page.gradeNumber,
				},
			});
			return;
		}

		setIsLeaderboardsModalOpen(true);
		dispatch({
			type: AT.OPEN_LEADERBOARDS_MODAL,
		});
	};

	const closeLeaderboardsModal = () => {
		setIsLeaderboardsModalOpen(false);
		dispatch({
			type: AT.CLOSE_LEADERBOARDS_MODAL,
		});
	};

	let settingsModal = null;
	if (isSettingsModalOpen) {
		settingsModal = (
			<SettingsModal
				onClose={closeSettingsModal}
				user={user}
				demo={page.demo}
			/>
		);
	}

	let gradeModal = null;
	if (isGradeModalOpen) {
		gradeModal = (
			<GradeModal
				onClose={() => setIsGradeModalOpen(false)}
				user={user}
				forChapter={true}
			/>
		);
	}

	const showHomework = _.get(user, "model.showHomework");
	const beamURL = _.get(user, "model.classURL");
	const navButtonClasses = {
		root: styles.horizontalNavButton,
		icon: styles.horizontalNavButtonIcon,
		label: styles.horizontalNavButtonLabel,
		underline: styles.horizontalNavButtonUnderline,
	};
	const beamButton = beamURL ? (
		<NavButton
			iconName={"beam"}
			label="BEAM"
			isExternal={true}
			clickSound={"top-bar-icon-1"}
			linkTo={beamURL}
			classes={navButtonClasses}
		/>
	) : null;
	const allChaptersButton = (
		<NavButton
			iconName={ICON_MAP.INDEX}
			label="All Chapters"
			clickSound={"top-bar-icon-1"}
			handleClick={() => {
				setIsGradeModalOpen(true);
				toggleMoreDropdown();
			}}
			classes={navButtonClasses}
		/>
	);
	const rankingButton =
		OpsUser.canViewLeaderboards(user) && !page.demo ? (
			<NavButton
				iconName={ICON_MAP.LEADERBOARDS}
				label="Ranking"
				clickSound={"top-bar-icon-1"}
				handleClick={() => {
					openLeaderboardsModal();
					toggleMoreDropdown();
				}}
				classes={navButtonClasses}
			/>
		) : null;
	const settingsButton = (
		<NavButton
			iconName={ICON_MAP.SETTINGS}
			label="Settings"
			clickSound={"top-bar-icon-1"}
			handleClick={() => {
				openSettingsModal();
				toggleMoreDropdown();
			}}
			classes={navButtonClasses}
		/>
	);
	const bugReportButton = OpsUser.canReportBugs(user) ? (
		<NavButton
			iconName={ICON_MAP.BUG}
			label="Report a Bug"
			clickSound={"top-bar-icon-1"}
			handleClick={() => {
				dispatch({
					type: AT.REPORT_BUG,
				});
				toggleMoreDropdown();
			}}
			classes={navButtonClasses}
		/>
	) : null;
	const logoutButton = (
		<NavButton
			iconName={ICON_MAP.LOGOUT}
			label="Sign Out"
			clickSound={"logout"}
			handleClick={() => {
				dispatch({
					type: AT.LOGOUT_USER_REQUEST,
				});
				toggleMoreDropdown();
			}}
			classes={navButtonClasses}
		/>
	);
	const moreDropdownModal = (
		<Modal
			backdropClass={styles.moreDropdownContainerBackdrop}
			modalClass={styles.moreDropdownContainer}
			hasCloseButton={false}
			closeOnBackdropClick
			closeOnEsc
			onClose={() => setIsMoreDropdownOpen(false)}
		>
			{allChaptersButton}
			{rankingButton}
			{settingsButton}
			{beamButton}
			{bugReportButton}
			{logoutButton}
		</Modal>
	);

	let leaderboardsModal = null;
	if (isLeaderboardsModalOpen) {
		const hasLeaderboardsAccess = OpsUser.canAccessLeaderboards(user);
		if (hasLeaderboardsAccess) {
			const isAcademy =
				OpsUser.isAcademyUser(user) ||
				OpsUser.isAcademyStaff(user) ||
				OpsUser.hasOpsAcademyRole(user);
			leaderboardsModal = (
				<LeaderboardsModal
					onClose={closeLeaderboardsModal}
					user={user}
					demo={page.demo}
					isAcademyUser={isAcademy}
				/>
			);
		} else {
			leaderboardsModal = (
				<MessageModal
					styleType="default"
					levelType="aboveAll"
					size={"extra-extra-large"}
					onClose={closeLeaderboardsModal}
				>
					<LeaderboardsLocked />
				</MessageModal>
			);
		}
	}

	return (
		<>
			<div className={styles.leftSideNav}>
				<img
					className={styles.logo}
					src={AssetsUtil.getLogo("aops-logo-red")}
				/>
				<LeftSideNavButton
					iconSrc={AssetsUtil.getGeneralIcon("map")}
					label="Campus"
					linkTo={URLMake.forHome()}
					clickSound={"top-bar-icon-1"}
					isSelected={page.name === "home.main"}
				/>
				<LeftSideNavButton
					iconSrc={AssetsUtil.getGeneralIcon("button-bell-blue")}
					label="Class"
					linkTo={classURL}
					clickSound={"top-bar-icon-1"}
					isSelected={OpsPage.isInTrainer(page)}
				/>
				{!page.demo && (
					<LeftSideNavButton
						iconSrc={AssetsUtil.getGeneralIcon("puzzle-piece")}
						label="Puzzle Lab"
						linkTo={URLMake.forLabLobby()}
						clickSound={"top-bar-icon-3"}
						isSelected={OpsPage.isInLab(page)}
					/>
				)}
				<LeftSideNavButton
					iconSrc={AssetsUtil.getGeneralIcon("library")}
					label="Library"
					linkTo={libraryURL}
					clickSound={"top-bar-icon-2"}
					isSelected={OpsPage.isInLibrary(page) || OpsPage.isInTheater(page)}
				/>
				<LeftSideNavButton
					icon={
						<Avatar
							hasSurround={true}
							surroundColor={"blue"}
							className={styles.avatar}
							avatar={
								page.demo ? dbDemoData.avatarModel : user.model?.avatar ?? {}
							}
						/>
					}
					label="Profile"
					linkTo={URLMake.forProfileDefault(user)}
					clickSound={"top-bar-icon-1"}
					isSelected={
						(OpsPage.isProfilePage(page) || OpsPage.isInBeastBuilder(page)) &&
						!OpsPage.isOnHomeworkTab(page)
					}
				/>
				{showHomework && (
					<LeftSideNavButton
						// TODO(jaketrower): Should implement notification...
						iconSrc={AssetsUtil.getGeneralIcon("hw-no-notification")}
						label="HW"
						linkTo={URLMake.forHomework()}
						clickSound={"top-bar-icon-2"}
						isSelected={OpsPage.isOnHomeworkTab(page)}
					/>
				)}
				<LeftSideNavButton
					iconSrc={AssetsUtil.getGeneralIcon("hamburger")}
					label="More"
					handleClick={toggleMoreDropdown}
					clickSound={"top-bar-icon-3"}
				/>
				<div
					className={styles.copyright}
					style={
						FLAGS.cleanMode && FLAG_OPTIONS.cleanMode.misc.hideCopyright
							? {display: "none"}
							: {}
					}
				>
					© AoPS Inc. {year}
				</div>
			</div>
			{gradeModal}
			{settingsModal}
			{leaderboardsModal}
			<Portal container={PORTAL_CONTAINERS.LEFT_NAV_MORE_MODAL}>
				{isMoreDropdownOpen ? moreDropdownModal : null}
			</Portal>
		</>
	);
};
